import { useRef, useState } from 'react';
import { Alert, Button, Icon } from '@tb/ui';
import type { AssetUploadResponse } from '@tb/shared';
import { useT } from '../../i18n/LocaleProvider';
import { formatBytes } from '../../lib/format';

export interface AssetManagerProps {
  assets: AssetUploadResponse[];
  uploading: boolean;
  error: string | null;
  maxAssetBytes: number;
  onUpload: (file: File) => void;
  onRemove: (assetId: string) => void;
  copyable: boolean;
}

export function AssetManager({
  assets,
  uploading,
  error,
  maxAssetBytes,
  onUpload,
  onRemove,
  copyable,
}: AssetManagerProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const t = useT();

  const copyReference = async (assetId: string) => {
    const reference = `asset://${assetId}`;
    try {
      await navigator.clipboard.writeText(reference);
      setCopied(assetId);
      window.setTimeout(() => setCopied(null), 1600);
    } catch {}
  };

  return (
    <div className="tb-stack">
      <div className="tb-row tb-row--between">
        <span className="tb-label">{t('editor.files.title')}</span>
        <span className="tb-hint">
          {t('editor.files.limit', { size: formatBytes(maxAssetBytes) })}
        </span>
      </div>

      {error ? <Alert tone="danger">{error}</Alert> : null}

      {assets.length === 0 ? (
        <span className="tb-hint">{t('editor.files.empty')}</span>
      ) : (
        <div className="asset-grid">
          {assets.map((asset) => (
            <div className="asset-tile" key={asset.id}>
              {asset.mime.startsWith('image/') ? (
                <img src={asset.previewUrl} alt={asset.path} />
              ) : (
                <Icon name="file" size={22} />
              )}
              <button
                type="button"
                className="asset-tile__remove"
                aria-label={t('editor.files.remove', { name: asset.path })}
                onClick={() => onRemove(asset.id)}
              >
                <Icon name="close" size={13} />
              </button>
              {copyable ? (
                <button
                  type="button"
                  className="asset-tile__name"
                  onClick={() => void copyReference(asset.id)}
                  title="Copy asset:// reference"
                >
                  {copied === asset.id ? t('editor.files.copied') : asset.path}
                </button>
              ) : (
                <span className="asset-tile__name">{asset.path}</span>
              )}
            </div>
          ))}
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        hidden
        accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml,image/avif,image/x-icon,font/woff,font/woff2,application/pdf"
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) onUpload(file);
          event.target.value = '';
        }}
      />

      <Button
        variant="secondary"
        icon="upload"
        loading={uploading}
        onClick={() => inputRef.current?.click()}
      >
        {t('editor.files.upload')}
      </Button>
    </div>
  );
}
